/* src/components/AgentAnimation.js */
import React, { useEffect } from 'react'
import { motion, useMotionValue, useTransform } from 'framer-motion'

export default function AgentAnimation() {
  const mouseX = useMotionValue(window.innerWidth / 2)
  const mouseY = useMotionValue(window.innerHeight / 2)

  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseX.set(e.clientX)
      mouseY.set(e.clientY)
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [mouseX, mouseY])

  // Slightly wider reactive range for more vibrant movement
  const reactiveX = useTransform(mouseX, [0, window.innerWidth], [-15, 15])
  const reactiveY = useTransform(mouseY, [0, window.innerHeight], [-15, 15])

  return (
    <motion.svg
      width="100%"
      height="100%"
      viewBox="0 0 800 600"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 1,
      }}
    >
      <defs>
        <radialGradient id="glowLayered" cx="50%" cy="50%" r="50%">
          {/* increased inner glow opacity */}
          <stop offset="0%" stopColor="#5fe3ff" stopOpacity="0.35" />
          <stop offset="65%" stopColor="#5b8cff" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#00e5ff" stopOpacity="0.05" />
        </radialGradient>

        <linearGradient id="lineGradient" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="#00e5ff" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#7b61ff" stopOpacity="0.06" />
        </linearGradient>

        <radialGradient id="overlayDarken" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </radialGradient>

        <filter id="blurFilter">
          <feGaussianBlur in="SourceGraphic" stdDeviation="12" />
        </filter>
      </defs>

      {/* Subtle dark overlay */}
      <rect width="800" height="600" fill="url(#overlayDarken)" />

      {/* Central glow */}
      <motion.circle
        cx="400"
        cy="300"
        r="120"
        fill="url(#glowLayered)"
        style={{ filter: 'url(#blurFilter)' }}
        animate={{ rotate: [0, 360] }}
        transition={{ repeat: Infinity, duration: 25, ease: 'linear' }}
      />

      {/* Pulsing ring */}
      <motion.circle
        cx="400"
        cy="300"
        r="130"
        stroke="#00e5ff"
        strokeOpacity="0.1"
        strokeWidth="2"
        fill="none"
        animate={{ scale: [1, 1.1, 1], opacity: [0.06, 0.12, 0.06] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Orbiting dots */}
      {[0, 1, 2, 3].map((i) => (
        <motion.circle
          key={i}
          cx="400"
          cy="300"
          r={6 + i * 1.5}
          fill="#00e5ff"
          style={{ filter: 'blur(2px)', opacity: 0.2 }}
          animate={{
            x: [0, Math.cos(i + Math.random()) * (90 + i * 12), 0],
            y: [0, Math.sin(i + Math.random()) * (90 + i * 12), 0],
          }}
          transition={{ duration: 14 + i * 1.5, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut', delay: i }}
        />
      ))}

      {/* Reactive circles following cursor */}
      {[...Array(5)].map((_, i) => (
        <motion.circle
          key={i}
          cx="400"
          cy="300"
          r={4 + i}
          fill="#00e5ff"
          style={{ filter: 'blur(1.5px)', opacity: 0.2 }}
          animate={{ cx: reactiveX, cy: reactiveY }}
          transition={{ repeat: Infinity, duration: 6 + i * 2, repeatType: 'mirror', ease: 'easeInOut', delay: i * 0.3 }}
        />
      ))}

      {/* Faint connecting lines */}
      {[
        { x1: 360, y1: 260, x2: 440, y2: 340 },
        { x1: 400, y1: 180, x2: 400, y2: 420 },
        { x1: 350, y1: 350, x2: 450, y2: 250 },
      ].map((line, idx) => (
        <motion.line
          key={idx}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke="#00e5ff"
          strokeOpacity="0.08"
          strokeWidth="1"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.08, 0] }}
          transition={{ repeat: Infinity, duration: 6 + idx, ease: 'easeInOut', delay: idx }}
        />
      ))}

      {/* Wavy gradient path */}
      <motion.path
        d="M0,500 Q300,320 800,340"
        stroke="url(#lineGradient)"
        strokeWidth="3"
        style={{ opacity: 0.08 }}
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 14, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
      />

      {/* Twinkling dot */}
      <motion.circle
        cx="600"
        cy="330"
        r="4"
        fill="#00e5ff"
        style={{ filter: 'blur(2px)' }}
        animate={{ opacity: [0, 0.3, 0] }}
        transition={{ duration: 2, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' }}
      />
    </motion.svg>
  )
}
